import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type AudioDurationResult = {
  durationInSeconds: string;
  formattedDuration: string;
};

const UNKNOWN_DURATION_RESULT: AudioDurationResult = {
  durationInSeconds: "0",
  formattedDuration: "unknown duration",
};

const formatDurationResult = (
  durationInSeconds: number
): AudioDurationResult => {
  const roundedDuration = Math.max(0, Math.round(durationInSeconds));

  return {
    durationInSeconds: roundedDuration.toString(),
    formattedDuration:
      roundedDuration < 60
        ? `${roundedDuration} sec`
        : `${Math.round(roundedDuration / 60)} min`,
  };
};

const getDurationFromMediaMetadata = (
  audioFile: File
): Promise<AudioDurationResult> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    const objectUrl = URL.createObjectURL(audioFile);
    let isSettled = false;

    const cleanup = () => {
      clearTimeout(timeoutId);
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.removeAttribute("src");
      URL.revokeObjectURL(objectUrl);
    };

    const fail = (message: string) => {
      if (isSettled) return;
      isSettled = true;
      cleanup();
      reject(new Error(message));
    };

    const timeoutId = setTimeout(() => {
      fail("Timed out loading media metadata");
    }, 10000);

    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      if (isSettled) return;

      const duration = audio.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        fail("Media metadata duration unavailable");
        return;
      }

      isSettled = true;
      cleanup();
      resolve(formatDurationResult(duration));
    };

    audio.onerror = () => {
      fail("Failed to load media metadata");
    };

    audio.src = objectUrl;
    audio.load();
  });
};

const getDurationFromAudioContext = (
  audioFile: File
): Promise<AudioDurationResult> => {
  return new Promise((resolve) => {
    const AudioContext =
      window.AudioContext || (window as any).webkitAudioContext;

    if (!AudioContext) {
      resolve(UNKNOWN_DURATION_RESULT);
      return;
    }

    const reader = new FileReader();

    const timeoutId = setTimeout(() => {
      resolve(UNKNOWN_DURATION_RESULT);
    }, 5000);

    reader.onload = function (e) {
      if (!e.target?.result) {
        clearTimeout(timeoutId);
        resolve(UNKNOWN_DURATION_RESULT);
        return;
      }

      const audioContext = new AudioContext();
      const arrayBuffer = e.target.result as ArrayBuffer;

      audioContext.decodeAudioData(
        arrayBuffer,
        function (buffer) {
          clearTimeout(timeoutId);
          resolve(formatDurationResult(buffer.duration));
          void audioContext.close();
        },
        function () {
          clearTimeout(timeoutId);
          resolve(UNKNOWN_DURATION_RESULT);
          void audioContext.close();
        }
      );
    };

    reader.onerror = function () {
      clearTimeout(timeoutId);
      resolve(UNKNOWN_DURATION_RESULT);
    };

    reader.readAsArrayBuffer(audioFile);
  });
};

export const calculateDuration = async (
  audioFile: File
): Promise<AudioDurationResult> => {
  try {
    return await getDurationFromMediaMetadata(audioFile);
  } catch {
    return getDurationFromAudioContext(audioFile);
  }
};
